<template>
  <b-modal
    :visible="visible"
    :title="profile ? `Edit ${profile.name}` : 'New quota profile'"
    size="lg"
    no-close-on-backdrop
    @change="onVisibilityChange"
  >
    <div v-if="apiError" class="alert alert-danger" role="alert">
      {{ apiError.message }}
      <span v-if="apiError.code" class="d-block small text-muted mt-1">{{ apiError.code }}</span>
    </div>

    <!-- A name and a budget. The token allowance, requests per minute, message length,
         the response cap and the generation timeout are all still enforced on every
         message; none of them is a decision made in this form, so an edit leaves them
         untouched and a new profile takes the standard values. -->
    <div class="form-row">
      <div class="col-md-6 form-group">
        <label class="qp-label" for="qp-name">
          NAME
          <i
            v-b-tooltip.hover
            class="fa fa-info-circle qp-info"
            title="What this set of limits is called. Roles are given a profile by this name, so renaming one changes what people see against their role."
          ></i>
        </label>
        <input id="qp-name" v-model="form.name" type="text" class="form-control" maxlength="96" placeholder="standard" />
        <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name }}</div>
      </div>

      <div class="col-md-6 form-group mb-0">
        <label class="qp-label" for="qp-spend">
          MONTHLY SPEND
          <i
            v-b-tooltip.hover
            class="fa fa-info-circle qp-info"
            title="The most one person carrying this profile can spend in a calendar month. Leave it empty for no spend limit — the monthly token allowance still applies either way."
          ></i>
        </label>
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text">$</span>
          </div>
          <input id="qp-spend" v-model="form.spendLimitUnits" type="number" min="0" step="0.01" class="form-control" placeholder="No limit" />
        </div>
        <!-- A budget that cannot bind is worse than no budget, because it reads as
             protection. Said here, where the number is being chosen. -->
        <div v-if="spendLimitUnenforceable" class="text-warning small mt-1">
          The model in use has no rate recorded, so this limit cannot bind. It is saved
          either way, and starts binding once that model is priced.
        </div>
        <div v-if="errors.spendLimitUnits" class="text-danger small mt-1">{{ errors.spendLimitUnits }}</div>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="primary" :disabled="saving" @click="submit">
        <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>
        {{ profile ? "Save changes" : "Create profile" }}
      </b-button>

      <!-- Between save and cancel, and red: it is a real action with a blast radius —
           every role carrying this profile stops being able to send — rather than a
           setting to be toggled on the way past. -->
      <b-button
        v-if="profile && profile.isActive"
        variant="danger"
        :disabled="saving"
        @click="$emit('deactivate', profile)"
      >
        Deactivate
      </b-button>
      <b-button
        v-else-if="profile"
        variant="outline-success"
        :disabled="saving"
        @click="$emit('reactivate', profile)"
      >
        Reactivate
      </b-button>

      <b-button variant="outline-secondary" @click="close">Cancel</b-button>
    </template>
  </b-modal>
</template>

<script>
import AiAdminService from "@/services/aiAdminService";
import { describeApiError } from "@/utils/ai/apiError";
import { changedFields, microsToUnits, unitsToMicros } from "@/utils/ai/settings";

/**
 * The limits this form does not ask about are no longer sent at all — the backend
 * defaults them on creation, so requests per minute, message length, the response cap
 * and the generation timeout live in one place rather than being invented here.
 */
/**
 * The token backstop a new profile starts with.
 *
 * Still required by the create endpoint, and still enforced beneath the spend limit — it
 * is what holds when a model has no rate recorded. It is not asked for because a token
 * count is not how anyone decides an allowance: the same count is worth different money
 * on different models. Belongs in the backend's own defaults; raised on ENG-2247.
 */
const TOKEN_BACKSTOP = 2000000;

const DEFAULTS = {
  name: "",
  spendLimitUnits: null,
};

export default {
  name: "QuotaProfileModal",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** The profile being edited, or null to create one. */
    profile: {
      type: Object,
      default: null,
    },
    /**
     * Whether the model in use has a rate recorded.
     *
     * Only used to say whether a spend limit can actually bind. The limit itself is the
     * profile's own field, and prices are never set from this screen.
     */
    modelIsPriced: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: { ...DEFAULTS },
      errors: {},
      apiError: null,
      saving: false,
    };
  },

  computed: {
    spendLimitUnenforceable() {
      const limit = this.form.spendLimitUnits;
      const hasLimit = limit !== null && limit !== "" && Number(limit) > 0;
      return hasLimit && !this.modelIsPriced;
    },
  },

  watch: {
    // Reset on open rather than on close: a failed save leaves the modal up with the
    // values that failed, which is what you want to correct them.
    visible(open) {
      if (open) {
        this.reset();
      }
    },
  },

  methods: {
    reset() {
      this.errors = {};
      this.apiError = null;
      this.saving = false;
      this.form = this.profile
        ? {
            name: this.profile.name,
            spendLimitUnits: microsToUnits(this.profile.monthlySpendLimitMicros),
          }
        : { ...DEFAULTS };
    },

    onVisibilityChange(open) {
      if (!open) {
        this.close();
      }
    },

    close() {
      this.$emit("close");
    },

    /**
     * The same bounds the backend validates, checked here so a typo comes back as a
     * sentence next to the field rather than a 400 with a class-validator message.
     */
    validate() {
      const errors = {};
      const form = this.form;

      if (!form.name || !form.name.trim()) {
        errors.name = "A name is required.";
      } else if (form.name.length > 96) {
        errors.name = "96 characters at most.";
      }

      if (form.spendLimitUnits !== null && form.spendLimitUnits !== "" && Number(form.spendLimitUnits) < 0) {
        errors.spendLimitUnits = "Zero or more, or empty for no limit.";
      }

      this.errors = errors;
      return !Object.keys(errors).length;
    },

    async submit() {
      if (!this.validate()) {
        return;
      }

      this.saving = true;
      this.apiError = null;

      try {
        const edited = {
          name: this.form.name.trim(),
          // Null is a real value here: it removes the limit.
          monthlySpendLimitMicros: unitsToMicros(this.form.spendLimitUnits),
        };
        let response;

        if (this.profile) {
          // Only what changed, so two administrators editing different limits on the
          // same profile do not overwrite each other with whatever their form loaded.
          // The limits this form does not show are simply absent from the PATCH.
          // An edit never sends the token backstop, so whatever a profile carries is
          // left exactly as it was.
          const changes = changedFields(
            {
              name: this.profile.name,
              monthlySpendLimitMicros: this.profile.monthlySpendLimitMicros,
            },
            edited,
          );

          if (!Object.keys(changes).length) {
            this.close();
            return;
          }

          response = await AiAdminService.updateQuotaProfile(this.profile.id, changes);
        } else {
          response = await AiAdminService.createQuotaProfile({
            ...edited,
            monthlyTokenAllowance: TOKEN_BACKSTOP,
          });
        }

        this.$emit("saved", response.data);
      } catch (error) {
        this.apiError = describeApiError(
          error,
          "That profile could not be saved. Check the limits and try again.",
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.qp-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #5d6474;
}

.qp-info {
  margin-left: 4px;
  color: #a8afbd;
  cursor: help;
}

.qp-info:hover {
  color: #6eb644;
}

/* Same treatment as the settings form: the spinner sits against the border rather than
   floating inside the field. */
input[type="number"] {
  padding-right: 2px;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  margin: 0;
  height: 100%;
  opacity: 1;
}
</style>
